import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import Grid from '@material-ui/core/Grid';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { JournalistContext } from '../JournalistContext';
import { AuthContext } from '../../Authentication/AuthContext';
import JournalistService from '../../../Services/JournalistService';
import JournalistCategoryService from '../../../Services/JournalistCategoryService';
import { JournalistCategoryModel } from '../../../Models/JournalistCategoryModel';
import { journalistModelInitial } from '../../../Models/JournalistModel';
import { ErrorHelper } from '../../../shared/ErrorHelper';
import { IErrorItemResponse, IErrorResponse } from '../../../shared/ErrorResponse';
import { validateSpecialCharacter } from '../../../shared/operators';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { HomeContext } from '../../Home/HomeContext';
import { BasicPage } from '../../../shared/components/BasicPage';


const useStyles = makeStyles((theme: Theme) => createStyles({
    root: {
        display: 'flex',
        padding: theme.spacing(3),
        maxWidth: '1152px',
    },
    itens: {
        width: '100%',
        color: 'primary'
    },
    button: {
        width: '100%',
        textAlign: 'right'
    },
    containerTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    }
})
);

export function JournalistInput() {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const journalistContext = React.useContext(JournalistContext); 
    const homeContext = React.useContext(HomeContext); 

    const [hasSpecialCharacter, setHasSpecialCharacter] = React.useState(false);
    const [nameNotDefinedError, setNameNotDefinedError] = React.useState(false);
    const [journalistExistDialog, setJournalistExistDialog] = React.useState(false);
    const [categoryNotSelectedError, setCategoryNotSelectedError] = React.useState(false);
    const [selectedCategory, setSelectedCategory] = React.useState<JournalistCategoryModel | null>(null);
    const [journalistCategoriesList, setJournalistCategoriesList] = React.useState<JournalistCategoryModel[]>([]);

    React.useEffect(() => {
        homeContext.setOpenLoadingDialog(true);
        JournalistCategoryService.journalistCategoryService()
            .getAll()
            .then((res: AxiosResponse) => {
                if (res.data) 
                    setJournalistCategoriesList(res.data);
            })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    }, []);

    const hasError = () => {
        const errorJournalistName = !journalistContext.journalist.name;
        const errorCategory = !selectedCategory;
        const errorSpecialCharacter = validateSpecialCharacter(journalistContext.journalist.name ?? '');
        setNameNotDefinedError(errorJournalistName);
        setCategoryNotSelectedError(errorCategory);
        setHasSpecialCharacter(errorSpecialCharacter);

        return errorJournalistName || errorCategory || errorSpecialCharacter;
    };

    const handleSave = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);
            
            const journalistData = journalistContext.journalist;
            journalistData.userId = authContext.login.user.id;
            JournalistService.journalistService()
                .create(journalistData)
                .then(() => {
                    homeContext.showSuccess('O novo jornalista foi incluído com sucesso!');
                    journalistContext.setJournalist(journalistModelInitial);
                    setSelectedCategory(null);

                    homeContext.setOpenLoadingDialog(false);
                })
                .catch((error: AxiosError) => {
                    const errorResponse: IErrorResponse = ErrorHelper(error);
                    if (errorResponse.errorList.length > 0
                        && errorResponse.errorList.some((errorItem: IErrorItemResponse) => errorItem.property === 'JournalistExist')) {
                        setJournalistExistDialog(true);

                    } else {
                        homeContext.showError('Ops.. ocorreu um erro e não foi possível salvar o novo jornalista' +
                            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                    }
                })
                .finally(() => { homeContext.setOpenLoadingDialog(false); });
        }
    };

    const getName = () => {
        return (
            <Grid item xs={12}>
                <TextField
                    label="Nome do jornalista"
                    variant="outlined"
                    fullWidth
                    value={journalistContext.journalist.name}
                    onChange={(event) =>
                        journalistContext.setJournalist({ ...journalistContext.journalist, name: event.target.value })
                    }
                    className={classes.itens}
                    inputProps={{ maxLength: 100 }}
                    error={(nameNotDefinedError && !journalistContext.journalist.name) || hasSpecialCharacter}
                    helperText={(nameNotDefinedError && !journalistContext.journalist.name ? 'Campo obrigatório' : '')
                        || (hasSpecialCharacter ? 'Caracteres especiais não são permitidos' : '')} />
            </Grid>
        );
    };

    const getCategory = () => {
        return (
            <Grid item xs={12} sm={8}>
                <Autocomplete
                    id="journalist-category"
                    value={selectedCategory}
                    onChange={(_, value: JournalistCategoryModel | null) => {
                        setSelectedCategory(value);
                        journalistContext.setJournalist({ ...journalistContext.journalist, categoryId: value ? value.id : 0 });
                    }}
                    options={journalistCategoriesList}
                    getOptionLabel={(option: JournalistCategoryModel) => option.description}
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField 
                            {...params}
                            label="Categoria do jornalista"
                            variant="outlined"
                            error={categoryNotSelectedError && !selectedCategory}
                            helperText={categoryNotSelectedError && !selectedCategory ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getScore = () => {
        return (
            <Grid item xs={12} sm={4}>
                <TextField
                    label="Peso"
                    disabled
                    value={selectedCategory ? selectedCategory.score : ''}
                    variant="outlined"
                    className={classes.itens}
                    InputLabelProps={{ shrink: true }} />
            </Grid>
        );
    };

    const getSaveButton = () => {
        return (
            <Grid item xs={8} sm={12} className={classes.button}>
                <Button
                    onClick={handleSave}
                    variant='contained'
                    color='primary'
                    size='large'>
                    SALVAR JORNALISTA
                </Button>
            </Grid>
        );
    };

    const getDialogs = () => {
        return (
            <>
                <BasicConfirmDialog
                    title="Ops, esse jornalista já existe na base."
                    description={`Encontramos o jornalista ${journalistContext.journalist.name}` + 
                        `com a categoria ${selectedCategory?.description} na base.`}
                    handleSecondButton={() => setJournalistExistDialog(false)}
                    textSecondButton="OK, ENTENDI"
                    open={journalistExistDialog} />
            </>
        );
    };

    return (
        <BasicPage
            title="Cadastro de jornalista"
            subtitle="Digite o nome e insira a categoria do novo jornalista que deseja incluir na base."
            dialogs={ getDialogs() }>
            <>
                {getName()}
                {getCategory()}
                {getScore()}
                {getSaveButton()}
            </>
        </BasicPage>
    );
}